import React, {
  useContext, useState, useEffect, useRef,
} from 'react';
import classnames from 'classnames';

import { useWindowStore, useConfigStore, useMapStateStore } from '../../../../stores';
import SiteSeeContext from '../../../SiteSeeGuides/SiteSeeContext';
import { getPulseTarget, PULSE_TARGETS } from '../../../SiteSeeGuides/SiteSeeGuideData';

import MapControlsZoomMoreIcon from './MapControlsZoomMoreIcon';
import { ReactComponent as MobileMapControlsZoomMoreIcon } from './MobileMapControlsZoomMoreIcon.svg';

const MapControlsZoomMore = () => {
  const isMobile = useWindowStore((state) => state.isMobile);
  const mapConfig = useConfigStore((state) => state.mapConfig);
  const activeZoom = useMapStateStore((state) => state.activeZoom);
  const setActiveZoom = useMapStateStore((state) => state.setActiveZoom);
  const effectiveMaxZoom = useMapStateStore((state) => state.effectiveMaxZoom);

  const { siteSeeGuideId, siteSeeGuideMode } = useContext(SiteSeeContext);
  const isPulsing = isMobile && siteSeeGuideMode === 'tour' && getPulseTarget(siteSeeGuideId, isMobile) === PULSE_TARGETS.ZOOM_IN;

  // Get zoom settings - use effectiveMaxZoom (from styled map test) if available
  const configMaxZoom = mapConfig?.googleMapsConfig?.zooms?.max;
  const maxZoom = effectiveMaxZoom ?? configMaxZoom;

  // Track if we've hit effective max zoom (Google Maps won't zoom higher).
  // This detects when the overlay or other factors prevent zooming beyond a certain level,
  // even if the configured maxZoom is higher.
  const [ atEffectiveMax, setAtEffectiveMax ] = useState( false );
  const prevZoomRef = useRef( null );
  const hitMaxRef = useRef( false );

  // Detect when zoom bounces back from max - this means we're at effective max.
  useEffect( () => {
    const currZoom = Math.round( activeZoom ?? 0 );
    const prevZoom = prevZoomRef.current;

    // If we hit maxZoom (or tried to go past it), mark it
    if ( maxZoom && currZoom >= maxZoom ) {
      hitMaxRef.current = true;
    }

    // If we previously hit max and now zoom dropped back, we're at effective max
    if ( hitMaxRef.current && prevZoom != null && currZoom < prevZoom && currZoom >= ( maxZoom - 1 ) ) {
      setAtEffectiveMax( true );
    }

    // Reset if we zoom out below max
    if ( maxZoom && currZoom < maxZoom ) {
      setAtEffectiveMax( false );
      hitMaxRef.current = false;
    }

    prevZoomRef.current = currZoom;
  }, [ activeZoom, maxZoom ] );

  // Round activeZoom since Google Maps can return fractional values during zoom animations.
  const roundedZoom = Math.round( activeZoom ?? 0 );
  const disabled = activeZoom != null && maxZoom && ( roundedZoom >= maxZoom || atEffectiveMax );

  const zoomMore = ( e ) => {
    e.preventDefault();
    // Blur button to prevent browser scroll-to-focus behavior
    e.currentTarget.blur();
    // Use current zoom from store, or fallback to a reasonable default
    const currentZoom = activeZoom ?? 16;
    // Cap zoom to maxZoom to avoid requesting beyond limit
    const newZoom = Math.min( currentZoom + 1, maxZoom );
    if ( newZoom <= currentZoom ) {
      // Already at max
      setAtEffectiveMax( true );
      return;
    }
    setActiveZoom( newZoom );
  };

  return maxZoom ? (
    <button
      type="button"
      id="mapZoomMore"
      disabled={disabled}
      className={
        classnames( {
          mapControl: !isMobile,
          mobileMapControl: isMobile,
          'mapControl--disabled': !isMobile && disabled,
          'mobileMapControl--disabled': isMobile && disabled,
          'mobileMapControl--pulsing': isPulsing,
        } )
      }
      onClick={zoomMore}
    >
      {isMobile ? <MobileMapControlsZoomMoreIcon className="mobileMapControl__svg" /> : <MapControlsZoomMoreIcon /> }
    </button>
  ) : <></>;
};

export default MapControlsZoomMore;
